import { Controller } from "@hotwired/stimulus"
import { heicTo, isHeic } from "heic-to"

export default class extends Controller {
  static targets = ["fileInputsContainer", "existingFilesContainer"]
  static values = {
    maxFiles: { type: Number, default: 10 }
  }

  connect() {
    this.fileCount = 0
    this.activeObjectUrls = []
    this.addFileInput()
    this.setupFormValidation()
  }

  setupFormValidation() {
    const form = this.element.querySelector('form')
    if (form) {
      form.addEventListener('submit', (e) => this.validateFormBeforeSubmit(e))
    }
  }

  validateFormBeforeSubmit(event) {
    const titleInput = this.element.querySelector('input[name*="[title]"]')
    
    if (!titleInput || !titleInput.value || titleInput.value.trim() === '') {
      event.preventDefault()
      alert(this.element.dataset.titleRequiredText || 'Please fill in the document title')
      titleInput?.focus()
      return false
    }
  }

  disconnect() {
    // Cleanup all object URLs
    this.activeObjectUrls.forEach(url => URL.revokeObjectURL(url))
    this.activeObjectUrls = []
  }

  addFileInput() {
    if (this.fileCount >= this.maxFilesValue) return

    const wrapper = document.createElement('div')
    wrapper.className = 'file-upload-item mb-3'
    wrapper.dataset.fileIndex = this.fileCount
    
    wrapper.innerHTML = `
      <div class="file-selector border rounded p-3 text-center" style="cursor: pointer; background: #f8f9fa;">
        <i class="fa-solid fa-cloud-upload fa-2x text-muted mb-2"></i>
        <p class="mb-0 text-muted">${this.selectFileText}</p>
        <input type="file" 
               name="user_document[files][]" 
               accept="image/*,.pdf,.doc,.docx" 
               class="d-none"
               data-action="change->user-document-form#handleFileSelect"
               data-file-index="${this.fileCount}">
      </div>
      <div class="file-preview d-none mt-2"></div>
    `

    this.fileInputsContainerTarget.appendChild(wrapper)
    
    // Add click handler to file selector
    const selector = wrapper.querySelector('.file-selector')
    const input = wrapper.querySelector('input[type="file"]')
    selector.addEventListener('click', () => input.click())
    
    this.fileCount++
  }

  async handleFileSelect(event) {
    const input = event.target
    const file = input.files[0]
    if (!file) return

    const fileIndex = input.dataset.fileIndex
    const wrapper = this.fileInputsContainerTarget.querySelector(`[data-file-index="${fileIndex}"]`)
    const selector = wrapper.querySelector('.file-selector')
    const preview = wrapper.querySelector('.file-preview')

    // Hide selector, show preview
    selector.classList.add('d-none')
    preview.classList.remove('d-none')

    // Show preview based on file type
    if (file.type.startsWith('image/')) {
      await this.showImagePreview(file, preview, input)
    } else {
      this.showDocumentPreview(file, preview, input)
    }

    // Add another file input if under limit
    if (this.fileCount < this.maxFilesValue) {
      this.addFileInput()
    }
  }

  async showImagePreview(file, preview, input) {
    let blobForPreview = file
    let displayName = file.name

    try {
      // Check if HEIC and convert to JPEG for preview
      if (await this.shouldConvertHeic(file)) {
        // Show converting message
        preview.innerHTML = `
          <div class="alert alert-info">
            <i class="fa-solid fa-spinner fa-spin me-2"></i>
            Конвертация HEIC...
          </div>
        `
        
        blobForPreview = await this.convertHeicToJpeg(file)
        displayName = this.convertedFileName(file.name)
      }

      const imageUrl = URL.createObjectURL(blobForPreview)
      this.activeObjectUrls.push(imageUrl)

      preview.innerHTML = `
        <div class="card">
          <div class="card-body p-2">
            <div class="row align-items-center">
              <div class="col-auto">
                <img src="${imageUrl}" 
                     class="img-thumbnail cursor-pointer" 
                     style="max-height: 80px; max-width: 80px; object-fit: cover;"
                     data-bs-toggle="modal"
                     data-bs-target="#mediaModal"
                     data-media-url="${imageUrl}"
                     data-media-type="image">
              </div>
              <div class="col">
                <p class="mb-1 small"><strong>${this.escapeHTML(this.truncateFilename(displayName, 30))}</strong></p>
                <p class="mb-0 text-muted small">${this.formatFileSize(blobForPreview.size)}</p>
              </div>
              <div class="col-auto">
                <button type="button" 
                        class="btn btn-sm btn-outline-danger" 
                        data-action="click->user-document-form#removeFile"
                        data-file-index="${input.dataset.fileIndex}">
                  <i class="fa-solid fa-times"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      `
    } catch (error) {
      console.error('Image preview error:', error)
      preview.innerHTML = `
        <div class="alert alert-warning">
          <i class="fa-solid fa-exclamation-triangle me-2"></i>
          Ошибка конвертации HEIC. Файл будет загружен на сервер.
        </div>
      `
    }
  }

  async shouldConvertHeic(file) {
    try {
      if (await isHeic(file)) {
        return true
      }
    } catch (error) {
      console.warn('Unable to detect HEIC format via signature check:', error)
    }

    const extension = this.fileExtension(file.name)
    return ['heic', 'heif'].includes(extension)
  }

  async convertHeicToJpeg(file) {
    return await heicTo({
      blob: file,
      type: "image/jpeg",
      quality: 0.9
    })
  }

  fileExtension(name) {
    if (!name || !name.includes('.')) {
      return ''
    }
    return name.split('.').pop().toLowerCase()
  }

  convertedFileName(name) {
    const base = name.includes('.') ? name.substring(0, name.lastIndexOf('.')) : name
    const safeBase = base && base.trim().length > 0 ? base : 'converted-image'
    return `${safeBase}.jpg`
  }

  showDocumentPreview(file, preview, input) {
    const icon = this.getFileIcon(file.type, file.name)
    
    preview.innerHTML = `
      <div class="card">
        <div class="card-body p-2">
          <div class="row align-items-center">
            <div class="col-auto">
              <i class="fa-solid fa-${icon} fa-3x text-muted"></i>
            </div>
            <div class="col">
              <p class="mb-1 small"><strong>${this.truncateFilename(file.name, 30)}</strong></p>
              <p class="mb-0 text-muted small">${this.formatFileSize(file.size)}</p>
            </div>
            <div class="col-auto">
              <button type="button" 
                      class="btn btn-sm btn-outline-danger" 
                      data-action="click->user-document-form#removeFile"
                      data-file-index="${input.dataset.fileIndex}">
                <i class="fa-solid fa-times"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    `
  }

  removeFile(event) {
    const fileIndex = event.currentTarget.dataset.fileIndex
    const wrapper = this.fileInputsContainerTarget.querySelector(`[data-file-index="${fileIndex}"]`)
    
    if (wrapper) {
      wrapper.remove()
      this.fileCount--
    }
  }

  removeExistingFile(event) {
    event.preventDefault()
    const button = event.currentTarget
    const fileId = button.dataset.fileId
    const card = button.closest('.existing-file-card')
    
    // Find the form element - it's a child of the controller element
    const form = this.element.querySelector('form')
    if (!form) {
      console.error('Form not found')
      return
    }
    
    // Add hidden input to mark file for removal
    const hiddenInput = document.createElement('input')
    hiddenInput.type = 'hidden'
    hiddenInput.name = 'remove_file_ids[]'
    hiddenInput.value = fileId
    form.appendChild(hiddenInput)
    
    // Remove entire column (col-md-3) with animation
    const col = card.closest('[class*="col-"]')
    col.style.transition = 'opacity 0.3s'
    col.style.opacity = '0'
    
    setTimeout(() => col.remove(), 300)
  }

  getFileIcon(contentType, filename) {
    if (contentType === 'application/pdf' || filename.toLowerCase().endsWith('.pdf')) {
      return 'file-pdf'
    } else if (contentType.includes('wordprocessingml') || 
               filename.toLowerCase().endsWith('.doc') || 
               filename.toLowerCase().endsWith('.docx')) {
      return 'file-word'
    }
    return 'file'
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  truncateFilename(filename, maxLength) {
    if (filename.length <= maxLength) return filename
    const extension = filename.split('.').pop()
    const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'))
    const truncated = nameWithoutExt.substring(0, maxLength - extension.length - 4) + '...'
    return truncated + '.' + extension
  }

  escapeHTML(value) {
    const div = document.createElement('div')
    div.textContent = value ?? ''
    return div.innerHTML
  }

  get selectFileText() {
    return this.element.dataset.selectFileText || 'Выбрать файл для загрузки'
  }
}
